import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../components/Icon.tsx';
import { api } from '../../lib/api.ts';
import { downloadPdf } from '../documents/api.ts';
import { useAuthStore } from '../../store/auth.ts';
import { canStock } from '../../lib/roles.ts';
import { t } from '../../lib/strings.ts';
import { formatDate } from '../../lib/format.ts';
import type { PrStatus } from './api.ts';

interface PrItem {
  id: string;
  description: string;
  qtyRequired: number;
  qtyInStock: number | null;
  department: string | null;
  requiredDate: string | null;
  sortOrder: number;
}

interface PurchaseRequest {
  id: string;
  prNumber: string;
  status: PrStatus;
  signDocId: string | null;
  createdAt: string;
  items: PrItem[];
  gsr?: { id: string; gsrNumber: string };
}

type ItemEdit = { qtyInStock: string; qtyRequired: string };

const STATUS_LABELS: Record<PrStatus, string> = {
  DRAFT: t.prDetail.statusDraft,
  PENDING_SIGNATURE: t.prDetail.statusPendingSignature,
  APPROVED: t.prDetail.statusApproved,
};

function statusPill(status: PrStatus): string {
  switch (status) {
    case 'DRAFT': return 'pill pill-neutral';
    case 'PENDING_SIGNATURE': return 'pill pill-pending';
    case 'APPROVED': return 'pill pill-success';
  }
}

function itemToEdit(item: PrItem): ItemEdit {
  return {
    qtyInStock: item.qtyInStock != null ? String(item.qtyInStock) : '',
    qtyRequired: String(item.qtyRequired),
  };
}

interface Props {
  prId: string;
  gsrId?: string; // if provided, invalidate gsr query after mutations
}

export function PurchaseRequestPanel({ prId, gsrId }: Props): JSX.Element {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const mayEdit = canStock(role);
  const [sending, setSending] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, ItemEdit>>({});

  const { data: pr, isLoading } = useQuery({
    queryKey: ['purchase-request', prId],
    queryFn: async () => {
      const r = await api.get<{ purchaseRequest: PurchaseRequest }>(`/purchase-requests/${prId}`);
      return r.data.purchaseRequest;
    },
    refetchOnWindowFocus: true,
  });

  const invalidateBoth = async () => {
    await qc.invalidateQueries({ queryKey: ['purchase-request', prId] });
    if (gsrId) await qc.invalidateQueries({ queryKey: ['gsr', gsrId] });
  };

  const handleSendToSign = async () => {
    setError(null);
    setSending(true);
    try {
      const r = await api.post<{ editorPath: string }>(`/purchase-requests/${prId}/send-for-signing`);
      const editorPath = r.data.editorPath;
      const h = await api.post<{ url: string }>('/wapsign/handoff', { redirectPath: editorPath });
      await invalidateBoth();
      window.location.href = h.data.url;
    } catch {
      setError(t.prDetail.errorSend);
    } finally {
      setSending(false);
    }
  };

  const handleContinueSigning = async () => {
    if (!pr?.signDocId) {
      setError(t.prDetail.errorSend);
      return;
    }
    setError(null);
    setSending(true);
    try {
      const h = await api.post<{ url: string }>('/wapsign/handoff', {
        redirectPath: `/documents/${pr.signDocId}/edit`,
      });
      window.location.href = h.data.url;
    } catch {
      setError(t.prDetail.errorSend);
    } finally {
      setSending(false);
    }
  };

  const handleWithdraw = async () => {
    setError(null);
    setWithdrawing(true);
    try {
      await api.post(`/purchase-requests/${prId}/withdraw-signing`);
      await invalidateBoth();
    } catch {
      setError(t.prDetail.errorWithdraw);
    } finally {
      setWithdrawing(false);
    }
  };

  const handleSaveItems = async () => {
    if (!pr) return;
    setError(null);
    setSaving(true);
    try {
      const items = pr.items.map((item) => {
        const e = edits[item.id] ?? itemToEdit(item);
        return {
          id: item.id,
          qtyRequired: parseInt(e.qtyRequired, 10) || item.qtyRequired,
          qtyInStock: e.qtyInStock === '' ? null : parseInt(e.qtyInStock, 10),
        };
      });
      await api.patch(`/purchase-requests/${prId}/items`, { items });
      await invalidateBoth();
      setEdits({});
    } catch {
      setError(t.prDetail.errorSave);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = () => {
    void downloadPdf(`/documents/purchase-request/${prId}`, `pr-${prId}.pdf`);
  };

  const getEdit = (item: PrItem): ItemEdit => edits[item.id] ?? itemToEdit(item);
  const setEdit = (itemId: string, field: keyof ItemEdit, value: string) => {
    setEdits((prev) => {
      const base = prev[itemId] ?? itemToEdit(pr!.items.find((i) => i.id === itemId)!);
      return { ...prev, [itemId]: { ...base, [field]: value } };
    });
  };

  if (isLoading) {
    return <p className="status-label text-muted-foreground" role="status">{t.prDetail.loading}</p>;
  }

  if (!pr) {
    return <p className="text-sm text-destructive">{t.prDetail.notFound}</p>;
  }

  const isDraft = pr.status === 'DRAFT';
  const isPending = pr.status === 'PENDING_SIGNATURE';
  const hasEdits = Object.keys(edits).length > 0;
  const editable = isDraft && mayEdit;

  return (
    <div className="space-y-6">
      {/* PR header — mirrors GsrDetail's masthead meta line */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="mono-meta text-muted-foreground num">{pr.prNumber}</span>
        <span className={statusPill(pr.status)}>{STATUS_LABELS[pr.status]}</span>
        <span className="text-sm text-muted-foreground">
          {t.prDetail.createdAt} <span className="num">{formatDate(pr.createdAt)}</span>
        </span>
      </div>

      {/* Action row — primary (send/continue signing) + secondary (PDF, withdraw),
          same border-t + pt-4 rhythm as GsrDetail Tab 1's action row. */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        {mayEdit && isDraft && (
          <button
            onClick={() => void handleSendToSign()}
            disabled={sending}
            className="btn-primary gap-1.5"
          >
            <Icon name="send" size={14} />
            {sending ? t.prDetail.sending : t.prDetail.sendToSign}
          </button>
        )}

        {mayEdit && isDraft && hasEdits && (
          <button
            onClick={() => void handleSaveItems()}
            disabled={saving}
            className="btn-secondary"
          >
            {saving ? t.prDetail.saving : t.prDetail.saveChanges}
          </button>
        )}

        {mayEdit && isPending && (
          <>
            <button
              onClick={() => void handleContinueSigning()}
              disabled={sending}
              className="btn-primary gap-1.5"
            >
              <Icon name="send" size={14} />
              {sending ? t.prDetail.sending : t.prDetail.continueSigning}
            </button>
            <button
              onClick={() => void handleWithdraw()}
              disabled={withdrawing}
              className="btn-secondary gap-1.5"
            >
              <Icon name="restart_alt" size={14} />
              {withdrawing ? t.prDetail.withdrawing : t.prDetail.withdraw}
            </button>
          </>
        )}

        <button onClick={handleDownloadPdf} className="btn-secondary gap-1.5">
          <Icon name="download" size={14} />
          {t.prDetail.downloadPdf}
        </button>

        {pr.status === 'APPROVED' && (
          <span className="pill pill-success">{t.prDetail.approvedBadge}</span>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Items */}
      <section className="space-y-2">
        <h2 className="section-heading">
          <span className="status-label text-muted-foreground">
            {t.prDetail.itemsHeading} (<span className="num">{pr.items.length}</span>)
          </span>
        </h2>

        {/* Mobile: stacked cards */}
        <ul className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] md:hidden">
          {pr.items.map((item, idx) => {
            const e = getEdit(item);
            return (
              <li key={item.id} className="p-3.5 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-medium leading-snug">{item.description}</p>
                  <span className="mono-meta text-muted-foreground shrink-0 num">#{idx + 1}</span>
                </div>
                {editable ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground">{t.prDetail.colQtyInStock}</label>
                      <input type="number" className="input mt-0.5 w-full" value={e.qtyInStock} min={0} onChange={(ev) => setEdit(item.id, 'qtyInStock', ev.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">{t.prDetail.colQtyRequired}</label>
                      <input type="number" className="input mt-0.5 w-full" value={e.qtyRequired} min={1} onChange={(ev) => setEdit(item.id, 'qtyRequired', ev.target.value)} />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span>{t.prDetail.colQtyRequired}: <span className="num text-foreground">{item.qtyRequired}</span></span>
                    {item.qtyInStock != null && <span>{t.prDetail.colQtyInStock}: <span className="num text-foreground">{item.qtyInStock}</span></span>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {/* Desktop: table */}
        <div className="hidden overflow-x-auto rounded-3xl bg-[hsl(var(--chrome))] md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/[0.05]">
                <th className="p-3 text-start status-label text-muted-foreground">{t.prDetail.colNum}</th>
                <th className="p-3 text-start status-label text-muted-foreground">{t.prDetail.colDescription}</th>
                <th className="p-3 text-end status-label text-muted-foreground">{t.prDetail.colQtyRequired}</th>
                <th className="p-3 text-end status-label text-muted-foreground">{t.prDetail.colQtyInStock}</th>
                <th className="p-3 text-start status-label text-muted-foreground">{t.prDetail.colRequiredDate}</th>
              </tr>
            </thead>
            <tbody>
              {pr.items.map((item, idx) => {
                const e = getEdit(item);
                return (
                  <tr key={item.id} className="border-t border-black/[0.05] hover:bg-[hsl(var(--surface-high))] transition-colors">
                    <td className="p-3 mono-meta text-muted-foreground num">{idx + 1}</td>
                    <td className="p-3 font-medium">{item.description}</td>
                    <td className="p-3 text-end">
                      {editable ? (
                        <input type="number" className="input w-20 ms-auto" value={e.qtyRequired} min={1} onChange={(ev) => setEdit(item.id, 'qtyRequired', ev.target.value)} aria-label={t.prDetail.colQtyRequired} />
                      ) : (
                        <span className="tabular num">{item.qtyRequired}</span>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      {editable ? (
                        <input type="number" className="input w-20 ms-auto" value={e.qtyInStock} min={0} onChange={(ev) => setEdit(item.id, 'qtyInStock', ev.target.value)} aria-label={t.prDetail.colQtyInStock} />
                      ) : (
                        <span className="tabular num">{item.qtyInStock ?? '—'}</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground num">{item.requiredDate ? formatDate(item.requiredDate) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
